/**
 * Відновлення екрана при поверненні (UX-08): прокрутка контейнера — до першого малювання, коли
 * дані вже є (з кешу — того самого рендера), і фокус на рядку-якорі з оголошенням «<список>,
 * рядок N з M». Пам'ять — app/return-state.ts.
 *
 * Екран з кількома контейнерами прокрутки (стовпці дошки, панелі вкладок картки) — `useScrollPart`:
 * кожен контейнер пам'ятає свою прокрутку під ключем частини в пам'яті того самого екрана (аудит
 * якості № 23). Частина може змінюватися на тому самому контейнері (вкладка картки): тоді прокрутка
 * попередньої частини записується, а нової — відновлюється до малювання.
 */
import { type RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { announce } from '../ui/notify/index.ts';
import { isReturn, readPart, readScreen, savePart, saveScreen } from './return-state.ts';

/** Пауза після прокрутки перед записом у вкладку: не пишемо сховище на кожен кадр. */
const SAVE_AFTER_MS = 200;

interface ScreenReturnOptions {
  /** Ключ пам'яті екрана (`listScreenKey(view)`). */
  key: string;
  /** Контейнер, що прокручується. */
  container: RefObject<HTMLElement | null>;
  /** id рядків у порядку показу; `null` — даних ще немає. */
  rows: readonly string[] | null;
  /** Елемент рядка, що отримує фокус (посилання на картку). */
  focusTarget?: ((id: string) => HTMLElement | null) | undefined;
  /**
   * Фокус на рядок за номером — для віртуалізованого списку, де рядка-якоря ще немає в DOM: сам
   * прокручує до нього й ставить фокус. `false` — не вийшло, оголошення немає.
   */
  focusRow?: ((index: number) => boolean) | undefined;
  /** `false` — екран без пам'яті повернення (хук нічого не робить). */
  enabled?: boolean | undefined;
  /** Текст для читача екрана після повернення. */
  announceText: (n: number, total: number) => string;
}

export function useScreenReturn({
  key,
  container,
  rows,
  focusTarget,
  focusRow,
  announceText,
  enabled = true,
}: ScreenReturnOptions): void {
  const restored = useRef<string | null>(null);
  const latest = useRef({ focusTarget, focusRow, announceText });
  latest.current = { focusTarget, focusRow, announceText };

  // До малювання: інакше видно стрибок з верху списку на збережене місце.
  useLayoutEffect(() => {
    const el = container.current;
    if (!enabled || !el || !rows || restored.current === key) return;
    restored.current = key;
    const memory = readScreen(key);
    // Той самий контейнер для іншого подання (`/l/a` → `/l/b`): без пам'яті — з верху списку.
    el.scrollTop = memory?.scroll ?? 0;
    if (!memory?.anchor || !isReturn() || rows.length === 0) return;
    // Якоря вже немає (видалено, вибув, доступ втрачено) — сусідній рядок на тому самому місці.
    const at = rows.indexOf(memory.anchor);
    const index = at >= 0 ? at : Math.min(Math.max(memory.index ?? 0, 0), rows.length - 1);
    const { focusRow: byIndex, focusTarget: byId } = latest.current;
    if (byIndex) {
      if (!byIndex(index)) return;
    } else {
      const id = rows[index];
      const target = id ? (byId?.(id) ?? null) : null;
      if (!target) return;
      target.focus({ preventScroll: true });
      target.scrollIntoView?.({ block: 'nearest' });
    }
    announce(latest.current.announceText(index + 1, rows.length));
  }, [key, rows, container, enabled]);

  // Прокрутка: остання позиція — у пам'ять із паузою і обов'язково при виході з екрана.
  // Контейнер може з'явитися пізніше за компонент (екран чекає метаданих) — підписуємось, щойно
  // з'явились рядки.
  const hasRows = rows !== null;
  // biome-ignore lint/correctness/useExhaustiveDependencies: hasRows — момент появи контейнера
  useEffect(() => {
    const el = container.current;
    if (!enabled || !el) return;
    let last = el.scrollTop;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const save = () => {
      clearTimeout(timer);
      timer = undefined;
      saveScreen(key, { scroll: last });
    };
    const onScroll = () => {
      last = el.scrollTop;
      clearTimeout(timer);
      timer = setTimeout(save, SAVE_AFTER_MS);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('pagehide', save);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('pagehide', save);
      if (timer !== undefined) save();
    };
  }, [key, container, hasRows, enabled]);
}

interface ScrollPartOptions {
  /** Ключ пам'яті екрана (`boardScreenKey(view)`, `cardScreenKey(entity)`). */
  key: string;
  /** Частина екрана: стовпець (`<воронка>:<етап>`), панель вкладки (`<запис>:<вкладка>`). */
  part: string;
  /** Контейнер прокрутки частини; функція — коли контейнер залежить від розкладки. */
  container: RefObject<HTMLElement | null> | (() => HTMLElement | null);
  /** Вміст частини на місці (дані стовпця прийшли): раніше відновлювати нема куди. */
  ready?: boolean | undefined;
  /**
   * Коли відновлювати: `return` — лише при поверненні на екран («Назад», крихта; стовпці дошки),
   * `always` — ще й щоразу, як частина змінилась на живому екрані (перемикання вкладки картки).
   * Звичайний перехід на екран починає з верху.
   */
  restore?: 'return' | 'always';
  /** `false` — без пам'яті (хук нічого не робить). */
  enabled?: boolean | undefined;
}

const elementOf = (container: ScrollPartOptions['container']) =>
  typeof container === 'function' ? container() : container.current;

/**
 * Прокрутка однієї частини екрана: відновлення до малювання, запис з паузою і обов'язково, коли
 * частина зникає чи змінюється. Підписка — у `useLayoutEffect`: при зміні частини на тому самому
 * контейнері прибирання старої йде до подій прокрутки, що їх викличе новий вміст.
 */
export function useScrollPart({
  key,
  part,
  container,
  ready = true,
  restore = 'return',
  enabled = true,
}: ScrollPartOptions): void {
  const restored = useRef<string | null>(null);
  const latest = useRef(container);
  latest.current = container;
  // Контейнер, на якому висить підписка. Інший елемент (скелетон став списком, сітка картки змінила
  // контейнер прокрутки) — нова підписка: інакше прокрутка нового не записувалась би.
  const attached = useRef<HTMLElement | null>(null);
  const [epoch, setEpoch] = useState(0);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `epoch` — тригер нової підписки
  useLayoutEffect(() => {
    const el = elementOf(latest.current);
    attached.current = null;
    if (!enabled || !ready || !el) return;
    attached.current = el;
    const id = `${key}\n${part}`;
    if (restored.current !== id) {
      // Перша частина екрана — як при поверненні; далі (`always`) — щоразу, як частина змінилась.
      const later = restored.current !== null;
      restored.current = id;
      if ((restore === 'always' && later) || isReturn()) el.scrollTop = readPart(key, part) ?? 0;
    }
    let last = el.scrollTop;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const save = () => {
      clearTimeout(timer);
      timer = undefined;
      savePart(key, part, last);
    };
    const onScroll = () => {
      last = el.scrollTop;
      clearTimeout(timer);
      timer = setTimeout(save, SAVE_AFTER_MS);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('pagehide', save);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('pagehide', save);
      if (timer !== undefined) save();
    };
  }, [key, part, ready, restore, enabled, epoch]);
  // Після кожного коміту: елемент контейнера той самий? Ні — підписатися заново (один зайвий рендер).
  useLayoutEffect(() => {
    if (enabled && ready && elementOf(latest.current) !== attached.current) setEpoch((n) => n + 1);
  });
}
